import { Button, Table, Spinner } from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import { checkPermissionByKey } from "../utils/checkPermissionByKey";
import { config } from "../config/config";
import { getactiveusers } from "../config/url";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import axios from "axios";
import formatDate from "../utils/formatDate";
import formatPhoneNumber from "../utils/formatPhoneNumber";
import DatePicker from "react-datepicker"; // Add this import
import "react-datepicker/dist/react-datepicker.css"; // Add this import
import { FaSearch, FaCalendarAlt, FaTimesCircle } from "react-icons/fa";

const formatUserListId = (userItem) => {
  const appId = userItem?.userAutoGenId;
  if (appId != null && appId !== "") {
    return String(appId).padStart(4, "0");
  }
  const cocmPatientId = userItem?.cocmMeta?.patientId;
  if (cocmPatientId) {
    return String(cocmPatientId);
  }
  return "—";
};

const ActiveUsers = () => {
  const user = useSelector((state) => state?.user);

  const [Businesses, setBusinesses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentUsers, setCurrentUsers] = useState([]);
  const [filter, setFilter] = useState("Active");
  const [filterBusiness, setFilterBusiness] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [usersPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Add date filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;
  const canViewUsers = checkPermissionByKey(user, "userManagement", "UserList");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filter, startDate, endDate]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!canViewUsers) {
        setCurrentUsers([]);
        setTotalUsers(0);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({
          businessId: businessId || "",
          businessIdentifier: String(businessIdentifier),
          limit: String(usersPerPage),
          skip: String((currentPage - 1) * usersPerPage),
        });
        if (filter === "Active" || filter === "Inactive") {
          params.set("status", filter);
        }
        if (debouncedSearch && debouncedSearch.trim()) {
          params.set("search", debouncedSearch.trim());
        }
        if (startDate && endDate) {
          const fmt = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
          };
          params.set("startDate", fmt(startDate));
          params.set("endDate", fmt(endDate));
        }

        const response = await axios.get(
          `${getactiveusers}?${params.toString()}`,
          config
        );
        const rows = response.data?.data || [];
        setCurrentUsers(rows);
        setTotalUsers(response.data?.total ?? rows.length);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(err);
        setCurrentUsers([]);
        setTotalUsers(0);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [
    canViewUsers,
    businessId,
    businessIdentifier,
    currentPage,
    usersPerPage,
    filter,
    debouncedSearch,
    startDate,
    endDate,
  ]);

  const getBusiness = async () => {
    try {
      const request = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/business/getBusiness`,
        config
      );
      if (request.data.success) {
        setBusinesses(request.data.data);
      }
    } catch (error) {
      console.error("🚀 -> getBusiness -> error:", error);
    }
  };

  useEffect(() => {
    getBusiness();
  }, []);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Function to clear date filters
  const clearDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const navigate = useNavigate();

  return (
    <div className="app-page">
      <div className="app-page-card" style={{ overflow: "hidden" }}>
        <div className="app-page-card-header">
          <h2 className="app-page-title">Users</h2>
          <div className="app-page-card-filters">
            <div className="app-filter-search-wrap">
              <FaSearch className="app-filter-search-icon" />
              <input
                className="form-control app-filter-search"
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                type="text"
                value={search}
              />
            </div>
            <div className="position-relative" style={{ width: "fit-content" }}>
              <div
                className="form-control app-filter-control"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                <span>
                  {startDate && endDate
                    ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                    : "Join Date"}
                </span>
                <span className="d-inline-flex align-items-center gap-2">
                  <FaCalendarAlt size={13} style={{ opacity: 0.7 }} />
                  {(startDate || endDate) && (
                    <FaTimesCircle
                      size={13}
                      style={{ cursor: "pointer", opacity: 0.7 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearDateFilter();
                      }}
                    />
                  )}
                </span>
              </div>
              {showDatePicker && (
                <div
                  className="position-absolute bg-white shadow-sm rounded p-2 mt-1"
                  style={{ zIndex: 1000, right: 0 }}
                >
                  <div className="d-flex flex-column">
                    <div className="mb-2">
                      <label className="mb-1">Start Date</label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        className="form-control"
                        placeholderText="Start Date"
                        dateFormat="MM/dd/yyyy"
                      />
                    </div>
                    <div className="mb-2">
                      <label className="mb-1">End Date</label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        className="form-control"
                        placeholderText="End Date"
                        dateFormat="MM/dd/yyyy"
                      />
                    </div>
                    <div className="d-flex justify-content-between">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          clearDateFilter();
                          setShowDatePicker(false);
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowDatePicker(false)}
                        style={{ backgroundColor: "#064260" }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <select
              className="form-select app-filter-select"
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: "140px" }}
              value={filter}
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-3 text-muted">Loading users...</div>
          </div>
        ) : totalUsers === 0 ? (
          <div className="text-center p-4">No users found.</div>
        ) : (
          <>
            <div className="app-page-table-wrap">
              <Table
                className="admin-table mb-0"
                hover
                responsive
              >
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    {user?.user?.role === "business" ? (
                      <th>Access Code</th>
                    ) : (
                      <th>DOB</th>
                    )}
                    <th>Email</th>
                    <th>Join Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody
                  style={{
                    color: "#575757",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontWeight: 500,
                    textAlign: "left",
                  }}
                >
                  {currentUsers &&
                    currentUsers.map((userItem) => (
                      <tr key={userItem._id} style={{ backgroundColor: "white" }}>
                        <td>{formatUserListId(userItem)}</td>
                        <td>
                          {userItem.firstname + " " + userItem.lastname}
                        </td>

                        {user?.user?.role === "business" ? (
                          <td>
                            {userItem.access_codes &&
                            userItem.access_codes.length > 0
                              ? userItem.access_codes.join(", ")
                              : "No Access Codes"}
                          </td>
                        ) : (
                          <td>{formatDate(userItem.dob)}</td>
                        )}

                        <td>{formatPhoneNumber(userItem.email)}</td>
                        <td>{formatDate(userItem.createdAt)}</td>
                        <td>{userItem.isactive ? "Active" : "Inactive"}</td>
                        <td>
                          {checkPermissionByKey(
                            userItem,
                            "userManagement",
                            "UserList"
                          ) && (
                            <Button
                              className="app-view-btn"
                              onClick={async () => {
                                try {
                                  const response = await axios.post(
                                    `${process.env.REACT_APP_BASE_URL}/users/getAccessCodeList`,
                                    userItem.access_codes
                                  );

                                  navigate(`/users/${userItem._id}`, {
                                    state: {
                                      user: userItem,
                                      accessCodeList: response.data.data,
                                    },
                                  });
                                } catch (error) {
                                  console.error(
                                    "🚀 -> onClick={ -> error:",
                                    error
                                  );
                                }
                              }}
                            >
                              View
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
            <TablePaginationBar
              currentPage={currentPage}
              totalItems={totalUsers}
              rowsPerPage={usersPerPage}
              onPageChange={paginate}
              style={{ paddingLeft: "20px", paddingRight: "20px" }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ActiveUsers;
